import { Reveal } from "@/components/Reveal";
import { Play } from "lucide-react";
import { mediaUrl, imgUrl } from "@/lib/api";

// Grouped gallery for the Karm 108 flagship: keeps films together, social
// grids + posts together, campaign stills, and packaging in their own sections.
// Each item opens the shared case-study lightbox via onOpen(originalIndex).

function Sec({ label, children }) {
  return (
    <Reveal>
      <div className="grid grid-cols-1 md:grid-cols-12 gap-6 py-10 md:py-14 border-t border-border">
        <div className="md:col-span-3">
          <p className="text-xs uppercase tracking-[0.25em] text-accent">{label}</p>
        </div>
        <div className="md:col-span-9">{children}</div>
      </div>
    </Reveal>
  );
}

function ReelTile({ m, onOpen }) {
  return (
    <button
      onClick={() => onOpen(m._i)} data-cursor="hover" data-testid={`film-${m._i}`}
      className="group relative block w-full overflow-hidden rounded-xl border border-border bg-surface aspect-[9/16]"
    >
      <video src={mediaUrl(m.url)} poster={m.poster ? imgUrl(m.poster, 700) : undefined}
        muted playsInline preload="none"
        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]" />
      <span className="absolute inset-0 flex items-center justify-center">
        <span className="flex h-14 w-14 items-center justify-center rounded-full bg-black/55 backdrop-blur text-white ring-1 ring-white/30 group-hover:scale-110 transition-transform">
          <Play size={22} className="ml-1" fill="currentColor" />
        </span>
      </span>
    </button>
  );
}

export default function KarmGallery({ media = [], onOpen }) {
  const films = media.filter((m) => m.type === "video");
  const feed = media.filter((m) => m.group === "feed");
  const social = media.filter((m) => m.type !== "video" && m.group === "social");
  const packaging = media.filter((m) => m.type !== "video" && m.group === "packaging");
  const campaign = media.filter(
    (m) => m.type !== "video" && !["social", "feed", "packaging"].includes(m.group)
  );

  return (
    <>
      {campaign.length > 0 && (
        <Sec label="The campaign">
          <div className="grid grid-cols-1 gap-8">
            {campaign.map((m) => (
              <div key={m._i}>
                <button
                  onClick={() => onOpen(m._i)} data-cursor="hover" data-cursor-text="Zoom"
                  data-testid={`visual-${m._i}`}
                  className="group block overflow-hidden rounded-xl bg-surface mx-auto max-w-full"
                >
                  <img src={imgUrl(m.url, 1400)} alt={m.caption || "Karm 108"}
                    onLoad={(e) => { e.target.parentElement.style.maxWidth = e.target.naturalWidth + "px"; }}
                    className="w-full block transition-transform duration-700 group-hover:scale-[1.02]" />
                </button>
                {m.caption && <p className="text-sm text-muted2 mt-2 text-center">{m.caption}</p>}
              </div>
            ))}
          </div>
        </Sec>
      )}

      {films.length > 0 && (
        <Sec label="The reels">
          <div className="grid grid-cols-2 gap-3 md:gap-4 max-w-[560px]">
            {films.map((m) => <ReelTile key={m._i} m={m} onOpen={onOpen} />)}
          </div>
        </Sec>
      )}

      {(social.length > 0 || feed.length > 0) && (
        <Sec label="The feed">
          {social.length > 0 && (
            <div className="grid grid-cols-3 gap-2 md:gap-3">
              {social.map((m) => (
                <button key={m._i} onClick={() => onOpen(m._i)} data-cursor="hover" data-cursor-text="Read" data-testid={`social-${m._i}`}
                  className="group relative block w-full overflow-hidden rounded-lg bg-surface aspect-[4/5] ring-1 ring-border">
                  <img src={imgUrl(m.url, 700)} alt={m.caption || "Karm 108"} loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]" />
                </button>
              ))}
            </div>
          )}
          {feed.length > 0 && (
            <div className={social.length > 0 ? "mt-6 md:mt-8" : ""}>
              {feed.map((m) => (
                <button key={m._i} onClick={() => onOpen(m._i)} data-cursor="hover" data-cursor-text="Zoom" data-testid={`feed-${m._i}`}
                  className="group block mx-auto w-full max-w-[560px] overflow-hidden rounded-xl bg-surface ring-1 ring-border">
                  <img src={imgUrl(m.url, 900)} alt="Karm 108, Instagram feed" loading="lazy"
                    className="w-full block transition-transform duration-700 group-hover:scale-[1.02]" />
                </button>
              ))}
            </div>
          )}
        </Sec>
      )}

      {packaging.length > 0 && (
        <Sec label="Packaging">
          <div className="grid grid-cols-2 gap-2 md:gap-3 max-w-[720px]">
            {packaging.map((m) => (
              <button key={m._i} onClick={() => onOpen(m._i)} data-cursor="hover" data-cursor-text="Zoom" data-testid={`packaging-${m._i}`}
                className="group block overflow-hidden rounded-lg bg-surface aspect-[3/4] ring-1 ring-border">
                <img src={imgUrl(m.url, 700)} alt={m.caption || "Karm 108 packaging"} loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]" />
              </button>
            ))}
          </div>
        </Sec>
      )}
    </>
  );
}
